import { useTranslation } from "react-i18next";
import { Megaphone } from "lucide-react";
import type { OrganizationMemberRoleName } from "@multi-chat/shared";
import { buildReportCampaignsExportPath } from "../../api/reports-api.js";
import { isApiError } from "../../api/client.js";
import { ErrorState } from "../../components/common/ErrorState/index.js";
import { campaignReportEmptyKind } from "./report-empty-kind.js";
import { CAMPAIGN_REPORT_COLUMNS, type CampaignReportColumnId } from "./report-column-defs.js";
import { CampaignReportTable } from "./CampaignReportTable.js";
import { CampaignReportToolbar } from "./CampaignReportToolbar.js";
import { ReportColumnsPopover } from "./ReportColumnsPopover.js";
import { ReportExportMenu } from "./ReportExportMenu.js";
import { ReportPageChrome } from "./ReportPageChrome.js";
import { ReportPageHeader } from "./ReportPageHeader.js";
import { ReportRefreshButton } from "./ReportRefreshButton.js";
import { useCampaignReport } from "./use-campaign-report.js";
import { useReportTableLayout } from "./use-report-table-layout.js";
import { exportLocaleFromUi, useReportExport } from "./use-report-export.js";
import type { CampaignReportFilterValues } from "./CampaignReportFilters.js";

function campaignColumnLabel(t: (key: string) => string, id: CampaignReportColumnId): string {
  if (id === "date") return t("reports.campaigns.columns.activityAt");
  return t(`reports.campaigns.columns.${id}`);
}

export function CampaignReportPage({
  organizationId,
  role,
}: {
  organizationId: number;
  role: OrganizationMemberRoleName;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const report = useCampaignReport({ organizationId, role });
  const exportCtl = useReportExport();
  const layout = useReportTableLayout(CAMPAIGN_REPORT_COLUMNS);

  const onFiltersChange = (patch: Partial<CampaignReportFilterValues>) => {
    if ("status" in patch) report.setStatus(patch.status);
    if ("endpointId" in patch) report.setEndpointId(patch.endpointId);
    if ("template" in patch) report.setTemplate(patch.template);
  };

  const onExport = (format: "csv" | "xlsx") => {
    const path = buildReportCampaignsExportPath(organizationId, {
      fromDate: report.query.from,
      toDate: report.query.to,
      search: report.query.search || undefined,
      status: report.query.status,
      endpointId: report.query.endpointId,
      template: report.query.template,
      sort: report.query.sort,
      format,
      locale: exportLocaleFromUi(i18n.language),
    });
    void exportCtl.runExport({
      pathWithQuery: path,
      fallbackFilename: `campaign-report.${format}`,
    });
  };

  const errorMessage =
    report.error == null
      ? null
      : isApiError(report.error) && report.error.status === 0
        ? t("reports.campaigns.backendUnavailable")
        : isApiError(report.error) && report.error.status === 403
          ? t("reports.campaigns.forbidden")
          : t("reports.campaigns.loadFailed");

  return (
    <div
      className="flex h-full min-h-0 flex-col overflow-hidden"
      data-testid="reports-campaigns-page"
    >
      <ReportPageChrome>
        <ReportPageHeader
          icon={Megaphone}
          title={t("reports.campaigns.title")}
          description={t("reports.campaigns.helper")}
          actions={
            report.canAccess ? (
              <div className="flex items-center gap-2">
                <ReportRefreshButton
                  refreshing={Boolean(report.loading && report.list)}
                  disabled={report.loading}
                  onRefresh={report.refresh}
                  testId="reports-campaigns-refresh"
                />
                <div className="grid gap-1">
                  <ReportExportMenu
                    exporting={exportCtl.exporting}
                    onExport={onExport}
                    testId="reports-campaigns-export"
                  />
                  {exportCtl.exportError ? (
                    <p className="m-0 text-[0.75rem] text-danger" role="alert">
                      {exportCtl.exportError}
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null
          }
        />

        <CampaignReportToolbar
          query={report.query}
          dateFrom={report.dateFrom}
          dateTo={report.dateTo}
          options={report.options}
          filteredTotal={report.list?.totalCount ?? null}
          filtersActive={report.filtersActive}
          onSearchChange={report.setSearch}
          onDateRangeChange={report.setDateRange}
          onFiltersChange={onFiltersChange}
          onResetFilters={report.resetFilters}
          columnsSlot={
            <ReportColumnsPopover
              columnOrder={layout.columnOrder}
              hiddenColumns={layout.hiddenColumns}
              requiredColumns={CAMPAIGN_REPORT_COLUMNS.requiredColumns}
              labelFor={(id) => campaignColumnLabel(t, id)}
              onToggle={layout.toggleColumn}
              onMove={layout.moveColumn}
              onReorder={layout.reorderColumn}
              onReset={layout.resetColumns}
              testIdPrefix="reports-campaigns"
            />
          }
        />
      </ReportPageChrome>

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-4 py-3 md:overflow-hidden md:px-5">
        {errorMessage ? (
          <ErrorState
            layout="panel"
            message={errorMessage}
            onRetry={report.refresh}
            retryLabel={t("auth.retry")}
          />
        ) : null}

        <div className="flex min-h-[20rem] min-h-0 flex-1 flex-col md:min-h-0">
          <CampaignReportTable
            list={report.list}
            loading={report.loading}
            page={report.query.page}
            pageSize={report.query.pageSize}
            emptyKind={campaignReportEmptyKind(report.query)}
            visibleColumns={layout.visibleColumns}
            widths={layout.widths}
            onResizeColumn={layout.resizeColumn}
            onReorderColumn={layout.reorderColumn}
            onPageChange={report.setPage}
            onPageSizeChange={report.setPageSize}
            onResetFilters={report.resetFilters}
          />
        </div>
      </div>
    </div>
  );
}
